import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { Key, useCallback, useMemo } from 'react'
import { useElementItemShallowSelector } from '../ElementItemStore'
import { useShallowSelector } from '../Workspace2Store'
import { TreeDataMapper } from '../hooks'
import type { ContentItemViewNodesType } from '../types/contentItemViewNode.type'
import type { DataNode } from '../types/types'
import {
    findArticlesNotLoaded,
    findWithChild,
    getUpdatedLoadingArticles,
    scheduleIdleCallback,
} from '../utils'
import { useTreeDataProcessor } from './useTreeDataProcessor'

/**
 * Custom hook to map ContentItems and DTV models to a hierarchical tree structure
 * @param contentItemViewNodes - Optional array of content items with it's subsequent dtv data
 * @returns Tree data mapping with state and setter methods
 */
export const useTreeDataMapping = (
    contentItemViewNodes?: Partial<ContentItemViewNodesType>[]
) => {
    const {
        isOMorSMMPage,
        articlesLoading,
        activeTab,
        contentItemsView,
        expandedKeys,
        setArticlesLoading,
        setContentItemViewNodes,
        setExpandedKeys,
        setTreeData,
        setIsTreeMappingInProgress,
    } = useShallowSelector((s) => ({
        isOMorSMMPage: s.isContentTeamFromStudioPage,
        articlesLoading: s.articlesLoading,
        activeTab: s.activeTab,
        contentItemsView: s.contentItemViewNodes,
        expandedKeys: s.expandedKeys,
        setContentItemViewNodes: s.actions.setContentItemViewNodes,
        setExpandedKeys: s.actions.setExpandedKeys,
        setTreeData: s.actions.setTreeData,
        setArticlesLoading: s.actions.setArticlesLoading,
        setIsTreeMappingInProgress: s.actions.setIsTreeMappingInProgress,
    }))

    const {
        isProjectProductFamily,
        elementType,
    } = useElementItemShallowSelector((s) => ({
        isProjectProductFamily: s.elementItemValue.isProjectProductFamily,
        elementType: s.elementItemValue.elementType,
    }))

    /**
     * Creates a memoized array of content items:
     * - returns contentItemViewNodes if it's not empty,
     * - else, returns contentItems if it's not empty,
     * - otherwise returns an empty array
     */
    const memoContentItemViewNodes = useMemo(() => {
        let localContentItemNodes = contentItemViewNodes || []

        if (contentItemViewNodes?.length === 0 && contentItemsView.length > 0) {
            setContentItemViewNodes([])
        }

        if (
            contentItemsView?.length === 0 &&
            localContentItemNodes?.length > 0 &&
            !isOMorSMMPage
        ) {
            setContentItemViewNodes(localContentItemNodes)
        } else if (contentItemsView?.length > 0) {
            localContentItemNodes = contentItemsView as ContentItemViewNodesType[]
        }

        const isNonPartExcluded = (
            contentItem: Partial<ContentItemViewNodesType>
        ) => contentItem.textType !== NODE_TYPES.Part // non-Part content items

        return Array.from(
            new Map(
                localContentItemNodes
                    .filter(isNonPartExcluded)
                    .map((node) => [node.id, node])
            ).values()
        )
    }, [contentItemsView])

    // Creates a memoized array of content item IDs
    const memoContentItemViewIds = useMemo(
        () => memoContentItemViewNodes?.map((x) => x.id ?? '') ?? [],
        [memoContentItemViewNodes]
    )
    // Creates a memoized array of article content item IDs
    const memoArticleContentItemIds = useMemo(
        () =>
            memoContentItemViewNodes
                ?.filter((x) => x.textType === 'Article')
                ?.map((x) => x.id ?? '') ?? [],
        [memoContentItemViewNodes]
    )
    // Creates a memoized instance of TreeDataMapper
    const mapper = useMemo(
        () =>
            new TreeDataMapper(
                memoContentItemViewNodes,
                isOMorSMMPage,
                activeTab,
                isProjectProductFamily,
                elementType
            ),
        [memoContentItemViewNodes]
    )
    // Creates a memoized map of children IDs for article content items
    const memoArticleContentItemChildrenIds = useMemo(() => {
        const map = new Map<string, React.Key[]>()

        for (const articleId of memoArticleContentItemIds) {
            const childIds = mapper.findChildren(articleId)
            map.set(articleId, childIds)
        }

        return map
    }, [memoArticleContentItemIds, mapper])

    /**
     * Maps content items to tree nodes
     * - filter out non-linked articles
     * - sorted by 'order'
     * @param contentItems - Content items to map
     * @returns Tree nodes
     */
    const mapContentItemsToTreeNodes = useCallback(
        (contentItemsView: Partial<ContentItemViewNodesType>[]) => {
            // Set flag to indicate mapping is in progress
            setIsTreeMappingInProgress?.(true)

            const filteredNodes = []

            // combine map and filter in a single pass to avoid intermediate arrays
            for (const item of contentItemsView) {
                const node = mapper.buildTreeNodes(item)

                if (mapper.isLinkedArticle(node)) {
                    filteredNodes.push(node)
                }
            }

            return filteredNodes.sort((a, b) => a.order - b.order)
        },
        [mapper, setIsTreeMappingInProgress]
    )

    /**
     * Calculate tree nodes that have children (recursively) and is not expanded yet
     * @returns keys of Tree nodes that need to be expanded
     */
    const withKeysToExpand = useCallback(
        (treeNodes: DataNode[]) => {
            const expandedKeysSet = new Set(expandedKeys)
            const nonExpandedArticleKeys = new Set()

            for (const id of memoArticleContentItemIds) {
                // find non-expanded article keys efficiently
                if (!expandedKeysSet.has(id)) {
                    nonExpandedArticleKeys.add(id)
                }
            }

            const nodesWithChildren = findWithChild(treeNodes)
            const nodeKeysToExpand = nodesWithChildren.filter(
                (key) => !nonExpandedArticleKeys.has(key.toString())
            )

            return nodeKeysToExpand
        },
        [memoArticleContentItemIds, expandedKeys]
    )

    /**
     * Calculate tree nodes that need to be collapsed (All Articles)
     * @returns keys of Tree nodes (Articles) that need to be collapsed
     */
    const withKeysToCollapse = useCallback(
        (treeNodes: DataNode[]) => {
            const allArticleKeys = new Set(memoArticleContentItemIds)

            const nodesWithChildren = findWithChild(treeNodes)
            const nodeKeysToCollapse = nodesWithChildren.filter(
                (key) => !allArticleKeys.has(key.toString())
            )

            return nodeKeysToCollapse
        },
        [memoArticleContentItemIds]
    )

    /**
     * Calculate tree nodes that need to be fetched (Articles)
     * - When an article or any of its children is not loaded yet, we need to fetch it
     * @param keysToExpand - Keys of Tree nodes that need to be fetched
     */
    const withKeysToFetch = useCallback(
        (keysToExpand: string[]) => {
            // find the articles that don't have their children loaded
            const notLoadedArticleIds: string[] = findArticlesNotLoaded(
                memoContentItemViewIds,
                memoArticleContentItemChildrenIds
            )

            const notLoadedSet = new Set(notLoadedArticleIds)
            const keysToFetch = keysToExpand.filter((key) =>
                notLoadedSet.has(key)
            )

            return keysToFetch
        },
        [memoContentItemViewIds, memoArticleContentItemChildrenIds]
    )

    /**
     * Batch update tree data and expanded keys with deferred updates
     * to prevent UI blocking with large datasets
     * @param treeNodes - Tree nodes to update
     * @param keysToExpand - Keys of Tree nodes that need to be expanded
     */
    const batchUpdateTree = useCallback(
        (treeNodes: DataNode[], keysToExpand: Key[]) => {
            // Update tree data first during idle time
            scheduleIdleCallback(() => {
                setTreeData(treeNodes)

                // Then update expanded keys in the next idle callback
                scheduleIdleCallback(() => {
                    setExpandedKeys(keysToExpand)

                    // updated articles that are still loading
                    scheduleIdleCallback(() => {
                        const updatedLoadingArticles = getUpdatedLoadingArticles(
                            treeNodes,
                            keysToExpand,
                            articlesLoading
                        )
                        setArticlesLoading(updatedLoadingArticles)
                    }, 50)
                }, 50)

                // Clear flag after mapping is complete
                // Use requestAnimationFrame to ensure tree has time to render
                requestAnimationFrame(() => {
                    setIsTreeMappingInProgress?.(false)
                })
            }, 50)
        },
        [
            setTreeData,
            setExpandedKeys,
            setArticlesLoading,
            articlesLoading,
            setIsTreeMappingInProgress,
        ]
    )

    /**
     * Use the TreeDataProcessor hook to handle data processing
     * with optimized batching and UI responsiveness
     */
    const { mapTreeData } = useTreeDataProcessor({
        processorFn: (items) =>
            mapContentItemsToTreeNodes(
                items as Partial<ContentItemViewNodesType>[]
            ),
        onComplete: (treeNodes) => {
            const keysToExpand = withKeysToExpand(treeNodes)

            batchUpdateTree(treeNodes, keysToExpand)
        },
    })

    return {
        memoContentItemViewIds,
        memoArticleContentItemIds,
        memoContentItemViewNodes,
        withKeysToFetch,
        withKeysToCollapse,
        mapTreeData,
    }
}
